import React, { useState } from 'react';
import { useStripe, useElements, PaymentElement, AddressElement } from '@stripe/react-stripe-js';
import { AddressParam, StripePaymentElementOptions } from '@stripe/stripe-js';
import { Button as MantineButton, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { logInternalError } from '../../../../lib/errors';
import { ApplePayOption } from '../../../../lib/applePayRecurring';

export interface BillingDetailsPrefill {
  name?: string;
  /** Stripe's own params shape, with country pinned: the address element requires one to render. */
  address?: AddressParam & { country: string };
}

interface RecoverPaymentFormProps {
  onSuccess: (confirmationTokenId: string) => void;
  loading: boolean;
  submitLabel?: string;
  hidden?: boolean;
  onInteraction?: () => void;
  children?: React.ReactNode;
  /**
   * Apple Pay recurringPaymentRequest so the wallet issues a merchant token (MPAN) that can be
   * charged off-session for renewals. Without it Apple issues a device token (DPAN), which is
   * exactly the failure-prone token type these update/recover flows exist to replace.
   */
  applePay?: ApplePayOption;
  /**
   * Collect cardholder name and a full billing address. Recurly needs a complete address to store a
   * Stripe gateway token it will still accept mid-dunning, and for ~44% of accounts it has none on
   * file to fall back on. Off by default so the in-app recover-payment flow is unchanged.
   */
  collectBillingDetails?: boolean;
  /** Seeds the name and address fields; users can edit whatever we guessed. */
  billingDetailsPrefill?: BillingDetailsPrefill;
  /** Keeps Link out of the element, matching the native payment sheet. */
  hideLink?: boolean;
  layout?: StripePaymentElementOptions['layout'];
}

export default function RecoverPaymentForm({
  onSuccess,
  loading,
  submitLabel,
  hidden,
  onInteraction,
  children,
  applePay,
  collectBillingDetails = false,
  billingDetailsPrefill,
  hideLink = false,
  layout,
}: RecoverPaymentFormProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    setError(null);
    setSubmitting(true);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message ?? t('recover_payment_error'));
        return;
      }

      // With the PaymentElement told never to render name or address, Stripe requires the caller to
      // supply them, so an omission here fails confirmation outright rather than degrading.
      let billingDetails: BillingDetailsPrefill | undefined;
      if (collectBillingDetails) {
        const addressElement = elements.getElement('address');
        const { complete, value } = (await addressElement?.getValue()) ?? { complete: false, value: undefined };
        if (!complete || !value) {
          setError(t('billing_address_incomplete'));
          return;
        }
        // The element reports an omitted line2 as null; stripe's params type only accepts undefined.
        billingDetails = {
          name: value.name.trim(),
          address: { ...value.address, line2: value.address.line2 ?? undefined },
        };
      }

      const { error: tokenError, confirmationToken } = await stripe.createConfirmationToken({
        elements,
        params: {
          return_url: `${window.location.origin}/manage`,
          ...(billingDetails && { payment_method_data: { billing_details: billingDetails } }),
        },
      });

      if (tokenError) {
        setError(tokenError.message ?? t('recover_payment_error'));
        return;
      }

      if (confirmationToken?.id) {
        onSuccess(confirmationToken.id);
      }
    } catch (err) {
      logInternalError(err);
      setError(t('recover_payment_error'));
    } finally {
      // only covers token creation; the parent's `loading` prop drives the button during the
      // backend save, so it resets (and the error shows) instead of spinning forever on failure.
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)}>
      <Stack spacing={16}>
        {collectBillingDetails && (
          <>
            <Text fw={600} fz={16}>
              {t('billing_address')}
            </Text>
            <AddressElement
              onFocus={onInteraction}
              options={{
                mode: 'billing',
                ...(billingDetailsPrefill?.address && {
                  defaultValues: { name: billingDetailsPrefill.name, address: billingDetailsPrefill.address },
                }),
              }}
            />
            <Text fw={600} fz={16}>
              {t('payment_method')}
            </Text>
          </>
        )}
        {/*
          Children are extra payment methods (paypal) that belong to the same list as the element's
          own rows, so they are grouped at the appearance's `spacingGridRow` rather than left in the
          outer stack, where the wider gap read as a separate section.
        */}
        <Stack spacing={12}>
          <PaymentElement
            onFocus={onInteraction}
            options={{
              // Link is a wallet, not an excludable payment method type - passing it to
              // elements({ excludedPaymentMethodTypes }) throws an IntegrationError. Suppressed here
              // because its inline signup asks a signed-in customer for an email and phone this flow
              // never uses, on a page they reached to change one card.
              wallets: {
                applePay: 'auto',
                googlePay: 'auto',
                link: collectBillingDetails || hideLink ? 'never' : 'auto',
              },
              ...(layout && { layout }),
              ...(applePay && { applePay }),
              // prefills the element's own name, country and ZIP when it shows them
              ...(billingDetailsPrefill && {
                defaultValues: {
                  billingDetails: { name: billingDetailsPrefill.name, address: billingDetailsPrefill.address },
                },
              }),
              // The address fields above replace the element's own name, country and ZIP, which would
              // otherwise ask for part of the same details twice.
              ...(collectBillingDetails && { fields: { billingDetails: { name: 'never', address: 'never' } } }),
            }}
          />
          {children}
        </Stack>
        {error && (
          <Text fz={14} color="red">
            {error}
          </Text>
        )}
        {!hidden && (
          <MantineButton
            type="submit"
            color="dark"
            radius="lg"
            loading={submitting || loading}
            disabled={!stripe || !elements}
          >
            {submitLabel ?? t('recover_payment_submit')}
          </MantineButton>
        )}
      </Stack>
    </form>
  );
}
